/**
 * Route-level code splitting — portal/admin/heavy pages load on demand so the
 * public landing bundle stays small on first paint.
 */
import { lazy } from 'react';

export const Login = lazy(() => import('./Login'));
export const CreateProfile = lazy(() => import('./CreateProfile'));
export const VerifyPhone = lazy(() => import('./VerifyPhone'));
export const Profile = lazy(() => import('./Profile'));

export const AdminLayout = lazy(() => import('./components/AdminLayout'));
export const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
export const AdminOverview = lazy(() => import('./components/AdminOverview'));
export const GameCheck = lazy(() => import('./components/GameCheck'));
export const AdminAssemblies = lazy(() => import('./components/AdminAssemblies'));
export const Audit = lazy(() => import('./components/Audit'));
export const MatchSubmissions = lazy(() => import('./components/MatchSubmissions'));
export const MatchSubmitted = lazy(() => import('./components/MatchSubmitted'));
export const ReplaySubmissions = lazy(() => import('./components/ReplaySubmissions'));
export const ReplaysSubmitted = lazy(() => import('./components/ReplaysSubmitted'));
export const ChallengesAdmin = lazy(() => import('./components/ChallengesAdmin'));
export const VideoAnalysis = lazy(() => import('./components/VideoAnalysis'));
export const HighlightsReview = lazy(() => import('./components/HighlightsReview'));
export const VenueQRCodes = lazy(() => import('./components/VenueQRCodes'));

export const VenueOwnerLayout = lazy(() => import('./components/VenueOwnerLayout'));
export const VenueGames = lazy(() => import('./components/VenueGames'));
export const VenueGamesHistory = lazy(() => import('./components/VenueGamesHistory'));
export const VenueGamesCodes = lazy(() => import('./components/VenueGamesCodes'));
export const VenueReplays = lazy(() => import('./components/VenueReplays'));
export const VenueBookings = lazy(() => import('./components/VenueBookings'));
export const VenueSetupWelcome = lazy(() => import('./components/venue/VenueSetupWelcome'));
export const VenueSettings = lazy(() => import('./components/VenueSettings'));
export const VenueOwnerLogin = lazy(() => import('./components/VenueOwnerLogin'));

export const ShortLinkRedirect = lazy(() => import('./components/ShortLinkRedirect'));
export const ReplayLinkHandler = lazy(() => import('./components/ReplayLinkHandler'));
export const JoinLinkHandler = lazy(() => import('./components/JoinLinkHandler'));
export const LegacyJoinQueryRedirect = lazy(() =>
  import('./components/JoinLinkHandler').then((m) => ({ default: m.LegacyJoinQueryRedirect }))
);
export const UniversalLinkHandler = lazy(() => import('./components/UniversalLinkHandler'));
export const ThreeDSCallback = lazy(() => import('./components/ThreeDSCallback'));

export const AppDownload = lazy(() => import('./pages/AppDownload'));
export const UploadFootage = lazy(() => import('./pages/UploadFootage'));
export const PublicMatchView = lazy(() => import('./pages/PublicMatchView'));
export const HubHome = lazy(() => import('./pages/hub/HubHome'));
export const HubGame = lazy(() => import('./pages/hub/HubGame'));
export const PlayerGamePayment = lazy(() => import('./pages/PlayerGamePayment'));
export const LeagueTeamPayment = lazy(() => import('./pages/LeagueTeamPayment'));
export const AcademyCreditPayment = lazy(() => import('./pages/AcademyCreditPayment'));
export const VenueReplayLinks = lazy(() => import('./pages/VenueReplayLinks'));
export const TestSubscription = lazy(() => import('./pages/TestSubscription'));

export const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
export const PaymentDecline = lazy(() => import('./pages/PaymentDecline'));
export const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));

export const AcademyLogin = lazy(() => import('./components/AcademyLogin'));
export const AcademyLayout = lazy(() => import('./components/AcademyLayout'));
export const AcademyDashboard = lazy(() => import('./components/AcademyDashboard'));
export const AcademySchedule = lazy(() => import('./components/AcademySchedule'));
export const AcademyFreeReplay = lazy(() => import('./components/AcademyFreeReplay'));
export const AcademyShareGames = lazy(() => import('./components/AcademyShareGames'));
export const AcademyTeamDetails = lazy(() => import('./components/AcademyTeamDetails'));
export const AcademyPlayerDetails = lazy(() => import('./components/AcademyPlayerDetails'));
export const AcademyMatchDetails = lazy(() => import('./components/AcademyMatchDetails'));
export const AcademyTimestampAnalysis = lazy(() => import('./components/AcademyTimestampAnalysis'));
export const AcademySettings = lazy(() => import('./components/AcademySettings'));
export const AcademyDrills = lazy(() => import('./components/AcademyDrills'));
export const AcademyCreateDrill = lazy(() => import('./components/AcademyCreateDrill'));
export const AcademyTeamsHub = lazy(() => import('./components/AcademyTeamsHub'));
export const AcademyRoster = lazy(() => import('./components/AcademyRoster'));
export const AcademyLeagueInvites = lazy(() => import('./components/AcademyLeagueInvites'));
export const AcademyLeaguesHub = lazy(() => import('./components/AcademyLeaguesHub'));
export const AcademyLeagueDetail = lazy(() => import('./components/AcademyLeagueDetail'));
export const AcademyLeagueMatchCenter = lazy(() => import('./components/AcademyLeagueMatchCenter'));
export const AcademyLeagueTeamProfile = lazy(() => import('./components/AcademyLeagueTeamProfile'));
export const AcademyMatches = lazy(() => import('./components/AcademyMatches'));
export const AcademyTrainingDetails = lazy(() => import('./components/AcademyTrainingDetails'));
export const AcademyVideoSessions = lazy(() => import('./components/AcademyVideoSessions'));
export const AcademySessionBuilder = lazy(() => import('./components/AcademySessionBuilder'));
export const AcademySessionPresenter = lazy(() => import('./components/AcademySessionPresenter'));
export const SessionDesigner = lazy(() => import('./components/session-designer/SessionDesigner'));

export const JoinAcademy = lazy(() => import('./components/public/JoinAcademy'));
export const JoinAcademyLeague = lazy(() => import('./components/public/JoinAcademyLeague'));
export const AcademyCoachInviteSignIn = lazy(() => import('./components/academy/AcademyCoachInviteSignIn'));
export const AcademyCoachInviteProfileRedirect = lazy(() =>
  import('./components/public/JoinAcademy').then((m) => ({ default: m.AcademyCoachInviteProfileRedirect }))
);
export const LegacyAcademyJoinRedirect = lazy(() =>
  import('./components/public/JoinAcademy').then((m) => ({ default: m.LegacyAcademyJoinRedirect }))
);

export const AdminSignups = lazy(() => import('./components/admin/AdminSignups'));
export const AdminInvoices = lazy(() => import('./components/AdminInvoices'));

export const CommunityLogin = lazy(() => import('./components/CommunityLogin'));
export const CommunitySignup = lazy(() => import('./components/CommunitySignup'));
export const CommunityLayout = lazy(() => import('./components/CommunityLayout'));
export const CommunityDashboard = lazy(() => import('./components/CommunityDashboard'));
export const CommunitySchedule = lazy(() => import('./components/CommunitySchedule'));
export const CommunityMatches = lazy(() => import('./components/CommunityMatches'));
export const CommunityGrowthPage = lazy(() => import('./components/CommunityGrowthPage'));
export const CommunityStats = lazy(() => import('./components/CommunityStats'));
export const CommunityLeaderboard = lazy(() => import('./components/CommunityLeaderboard'));
export const CommunityChallenges = lazy(() => import('./components/CommunityChallenges'));
export const CommunityChallengeDetail = lazy(() => import('./components/CommunityChallengeDetail'));
export const CommunityRetentionDeepDive = lazy(() => import('./components/CommunityRetentionDeepDive'));
export const CommunityGameSuccessDeepDive = lazy(() => import('./components/CommunityGameSuccessDeepDive'));
export const CommunityDiscoveryAnalytics = lazy(() => import('./components/CommunityDiscoveryAnalytics'));
export const CommunityEngagementDeepDive = lazy(() => import('./components/CommunityEngagementDeepDive'));
export const CommunityPlayerManagement = lazy(() => import('./components/CommunityPlayerManagement'));
export const CommunityJoinRequests = lazy(() => import('./components/CommunityJoinRequests'));
export const CommunityGroups = lazy(() => import('./components/CommunityGroups'));
export const ManagementHub = lazy(() => import('./components/ManagementHub'));
export const CommunityPlayerDetails = lazy(() => import('./components/CommunityPlayerDetails'));
export const CommunityMatchDetails = lazy(() => import('./components/CommunityMatchDetails'));
export const CommunityVideoLibrary = lazy(() => import('./components/CommunityVideoLibrary'));
export const ConfigCommunity = lazy(() => import('./components/ConfigCommunity'));
export const ConfigSettings = lazy(() => import('./components/ConfigSettings'));
export const CommunityActionRequired = lazy(() => import('./components/CommunityActionRequired'));
export const CommunityCompetitions = lazy(() => import('./components/CommunityCompetitions'));
export const CreateCompetition = lazy(() => import('./components/CreateCompetition'));
export const CompetitionDetail = lazy(() => import('./components/CompetitionDetail'));
export const BuyCreditsSecure = lazy(() => import('./components/BuyCreditsSecure'));
export const AcademyTeamSubscribePage = lazy(() => import('./components/academy/AcademyTeamSubscribePage'));
export const JoinCommunityManager = lazy(() => import('./components/public/JoinCommunityManager'));
export const CommunityManagerInviteSignIn = lazy(() => import('./components/community/CommunityManagerInviteSignIn'));

export const AcademyManagement = lazy(() => import('./components/AcademyManagement'));
export const CommunityManagement = lazy(() => import('./components/CommunityManagement'));
export const AdminReferral = lazy(() => import('./components/admin/AdminReferral'));
export const AdminEvents = lazy(() => import('./components/admin/AdminEvents'));
export const EventBoothRoute = lazy(() =>
  import('./components/public/ExperiaBoothPage').then((m) => ({ default: m.EventBoothRoute }))
);
export const VenueReferralPage = lazy(() => import('./components/public/VenueReferralPage'));
export const AdminApiKeys = lazy(() => import('./components/admin/AdminApiKeys'));
export const AdminApiHistory = lazy(() => import('./components/admin/AdminApiHistory'));
export const AdminPadelytics = lazy(() => import('./components/admin/AdminPadelytics'));
export const AdminPadelyticsSession = lazy(() => import('./components/admin/AdminPadelyticsSession'));
export const AdminSentMail = lazy(() => import('./components/admin/AdminSentMail'));
export const ApiDocumentation = lazy(() => import('./pages/docs/ApiDocumentation'));
